
import { create } from 'zustand';
import { User, Session } from '@supabase/supabase-js';

export type UserRole = 'admin' | 'coach' | 'athlete' | 'super_admin' | null;

export interface AvailableGym {
  id: string;
  name: string;
  isPrimary: boolean;
}

interface UserState {
  role: UserRole;
  gymId: string | null;
  availableGyms: AvailableGym[];
  isLoadingGymSwitch: boolean;
  setRole: (role: UserRole) => void;
  setGymId: (gymId: string | null) => void;
  setAvailableGyms: (gyms: AvailableGym[]) => void;
  switchGym: (gymId: string) => void;
  clearRole: () => void;
  clearAuth: () => void;
  user: User | null;
  session: Session | null;
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setIsLoadingGymSwitch: (loading: boolean) => void;
}

export const useUserStore = create<UserState>((set) => ({
  role: null,
  gymId: null,
  availableGyms: [],
  user: null,
  session: null,
  isLoadingGymSwitch: false,
  setRole: (role) => set({ role }),
  setGymId: (gymId) => set({ gymId }),
  setAvailableGyms: (availableGyms) => set({ availableGyms }),
  switchGym: async (newGymId) => {
    const userId = useUserStore.getState().user?.id;
    const currentGymId = useUserStore.getState().gymId;
    
    console.log('[userStore] Switching gym from', currentGymId, 'to', newGymId);
    
    // ✅ ÉTAPE 1 : Invalider TOUS les caches AVANT le switch
    if (userId && currentGymId) {
      try {
        // Invalider le cache du schedule service
        const { scheduleClassesService } = await import('@/services/scheduleClassesService');
        scheduleClassesService.invalidateCache(userId, currentGymId);
        scheduleClassesService.invalidateAllCaches();
        console.log('[userStore] Schedule cache invalidated');
        
        // Vider le cache des clients
        const { useClientsSupabaseStore } = await import('@/store/api/clientsSupabaseStore');
        useClientsSupabaseStore.getState().clearClients();
        console.log('[userStore] Clients cache cleared');

        // Vider la liste des clients du formulaire de paiement
        const { useUsersSupabaseStore } = await import('@/store/api/usersSupabaseStore');
        useUsersSupabaseStore.getState().reset();
        
        // Vider le cache des bookings
        const { useBookingsSupabaseStore } = await import('@/store/api/bookingsSupabaseStore');
        useBookingsSupabaseStore.getState().clearBookings?.();
        console.log('[userStore] Bookings cache cleared');
      } catch (cacheError) {
        console.warn('[userStore] Cache invalidation warning:', cacheError);
      }
    }

    // ✅ Vider le cache react-query, quel que soit l'état d'hydratation.
    // Plusieurs clés de requête n'incluent pas le `gym_id` : sans ce vidage,
    // les données de la salle précédente restaient servies depuis le cache.
    try {
      const { queryClient } = await import('@/lib/queryClient');
      queryClient.clear();
      console.log('[userStore] react-query cache cleared');
    } catch (queryCacheError) {
      console.warn('[userStore] react-query cache clear warning:', queryCacheError);
    }
    
    // ✅ ÉTAPE 2 : Mettre à jour le gymId et déclencher le loading
    set({ isLoadingGymSwitch: true, gymId: newGymId });
    localStorage.setItem('selected_gym_id', newGymId);
    
    try {
      // ✅ Recharger les rôles pour le nouveau gym
      const { supabase } = await import('@/integrations/supabase/client');
      const { getPrimaryRole } = await import('@/hooks/useUserRoles');
      
      if (userId) {
        const { data: rolesData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .eq('gym_id', newGymId);
        
        const userRoles = (rolesData || []).map(r => r.role);
        const newRole = getPrimaryRole(userRoles as any[]);
        
        set({ role: newRole });
        
        // ✅ Recharger les settings pour le nouveau gym
        const { useSettingsStore } = await import('@/store/api/settingsStore');
        await useSettingsStore.getState().fetchSettings();
        
        // ✅ Navigation fluide sans reload complet
        const targetPath = newRole === 'admin' ? '/admin/dashboard' 
          : newRole === 'coach' ? '/coach/schedule' 
          : '/athlete/dashboard';
        
        window.history.pushState({}, '', targetPath);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        console.warn('[userStore] switchGym sans utilisateur hydraté : rôles et réglages non rechargés');
      }
    } catch (error) {
      console.error('Error switching gym:', error);
    } finally {
      // ⚠️ Le `false` n'était posé que dans la branche `if (userId)` : si
      // l'utilisateur n'était pas encore hydraté, le spinner plein écran
      // restait affiché indéfiniment (audit lot 5).
      set({ isLoadingGymSwitch: false });
    }
  },
  clearRole: () => set({ role: null }),
  clearAuth: () => {
    set({ role: null, gymId: null, availableGyms: [], user: null, session: null, isLoadingGymSwitch: false });
    
    try {
      localStorage.removeItem('selected_gym_id');
      
      // Purge ciblée des clés Supabase
      const SB_KEY = 'sb-fgjjiyeumlgduhrdvdto-auth-token';
      localStorage.removeItem(SB_KEY);
      
      // Par sécurité, retirer toutes les clés qui commencent par 'sb-' ou 'supabase'
      for (const k of Object.keys(localStorage)) {
        if (k.startsWith('sb-') || k.startsWith('supabase')) {
          localStorage.removeItem(k);
        }
      }
      
      // Également sessionStorage par prudence
      for (const k of Object.keys(sessionStorage)) {
        if (k.startsWith('sb-') || k.startsWith('supabase')) {
          sessionStorage.removeItem(k);
        }
      }
    } catch (error) {
      console.warn('Storage cleanup warning:', error);
    }
  },
  setUser: (user) => set({ user }),
  setSession: (session) => set({ session }),
  setIsLoadingGymSwitch: (loading) => set({ isLoadingGymSwitch: loading })
}));
